<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { isCallerIdSummary, type CallerIdSummary } from '@calloneer/contracts';
import { apiRequest } from '../api.js';
import { useAudioStore } from '../stores/audio.js';
import { useCallStore } from '../stores/call.js';

const call = useCallStore();
const audio = useAudioStore();
const destination = ref('');
const callerIds = ref<readonly CallerIdSummary[]>([]);
const callerIdId = ref('');
const isCallerIds = (value: unknown): value is readonly CallerIdSummary[] =>
  Array.isArray(value) && value.every(isCallerIdSummary);
const statusLabel = computed(() => call.state.toLowerCase().replace('_', ' '));
const canSelectOutput = computed(() => typeof HTMLMediaElement.prototype.setSinkId === 'function');
const canDial = computed(
  () => destination.value.trim().length > 3 && callerIdId.value !== '' && !call.active,
);

const reconcileAudioPreferences = (): void => {
  const current = audio.preferences;
  const inputId =
    current.inputId && call.microphones.some((device) => device.id === current.inputId)
      ? current.inputId
      : null;
  const outputId =
    current.outputId && call.speakers.some((device) => device.id === current.outputId)
      ? current.outputId
      : null;
  if (inputId !== current.inputId || outputId !== current.outputId)
    audio.save({ ...current, inputId, outputId });
};
const refreshDevices = async (): Promise<void> => {
  await call.refreshDevices();
  reconcileAudioPreferences();
};
let deviceChangeHandler: (() => void) | undefined;
onMounted(async () => {
  [callerIds.value] = await Promise.all([
    apiRequest({ path: '/v1/me/caller-ids', guard: isCallerIds }),
    refreshDevices(),
  ]);
  callerIdId.value = callerIds.value[0]?.id ?? '';
  deviceChangeHandler = () => void refreshDevices();
  navigator.mediaDevices?.addEventListener('devicechange', deviceChangeHandler);
});
onBeforeUnmount(() => {
  if (deviceChangeHandler)
    navigator.mediaDevices?.removeEventListener('devicechange', deviceChangeHandler);
});
const dial = async (): Promise<void> =>
  call.start({
    destination: destination.value,
    callerIdId: callerIdId.value,
    audio: audio.preferences,
  });
const selectMicrophone = (id: string): void => {
  audio.save({ ...audio.preferences, inputId: id || null });
  call.selectMicrophone(id);
};
const selectSpeaker = (id: string): void => {
  audio.save({ ...audio.preferences, outputId: id || null });
  call.selectSpeaker(id);
};
</script>

<template>
  <div class="page-grid dialer-grid">
    <section class="panel dial-panel">
      <p class="eyebrow">OUTBOUND AUDIO</p>
      <h1>Make a call</h1>
      <div
        v-if="call.state === 'IDLE' || call.state === 'FAILED' || call.state === 'ENDED'"
        class="dial-form"
      >
        <label
          >Phone number<input
            v-model="destination"
            autocomplete="tel"
            inputmode="tel"
            placeholder="+1 415 555 0132"
        /></label>
        <label
          >Caller ID<select v-model="callerIdId">
            <option disabled value="">Choose a verified number</option>
            <option v-for="item in callerIds" :key="item.id" :value="item.id">
              {{ item.label }} · {{ item.phoneNumber }}
            </option>
          </select></label
        >
        <p v-if="callerIds.length === 0" class="notice">
          No usable caller ID yet. Finish Voximplant setup in Settings.
        </p>
        <p v-if="call.error" class="error">{{ call.error }}</p>
        <button v-if="call.state !== 'IDLE'" class="secondary" @click="call.reset">New call</button>
        <button class="primary call-button" :disabled="!canDial" @click="dial">Call now</button>
      </div>
      <div v-else class="active-call">
        <div class="pulse"><span></span></div>
        <p class="status">{{ statusLabel }}</p>
        <h2>{{ destination }}</h2>
        <div class="keypad">
          <button
            v-for="tone in ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']"
            :key="tone"
            :disabled="call.state !== 'CONNECTED'"
            @click="call.sendDtmf(tone)"
          >
            {{ tone }}
          </button>
        </div>
        <div class="call-controls">
          <button class="secondary" @click="call.toggleMute">
            {{ call.muted ? 'Unmute' : 'Mute' }}</button
          ><button class="danger" @click="call.hangup">End call</button>
        </div>
      </div>
    </section>
    <aside class="panel compact">
      <p class="eyebrow">AUDIO ROUTING</p>
      <h2>Current devices</h2>
      <label
        >Microphone<select
          :value="audio.preferences.inputId ?? ''"
          @change="selectMicrophone(($event.target as HTMLSelectElement).value)"
        >
          <option value="">System default</option>
          <option v-for="item in call.microphones" :key="item.id" :value="item.id">
            {{ item.label || 'Microphone' }}
          </option>
        </select></label
      ><label
        >Speaker<select
          :value="audio.preferences.outputId ?? ''"
          :disabled="!canSelectOutput"
          @change="selectSpeaker(($event.target as HTMLSelectElement).value)"
        >
          <option value="">System default</option>
          <option v-for="item in call.speakers" :key="item.id" :value="item.id">
            {{ item.label || 'Speaker' }}
          </option>
        </select></label
      >
      <p v-if="!canSelectOutput" class="notice">
        This browser does not support selecting a separate speaker.
      </p>
      <p class="fine-print">Device choices are stored only in this browser.</p>
    </aside>
  </div>
</template>
